import { type Signal } from '@angular/core';
import { type EditorComponent } from '@tinymce/tinymce-angular';

import { isNullish, setCssPropertyValue } from '@blaze-shared/shared-util';

export function initializeTinyMceConfig(params: {
  initHandler: () => void;
  blurHandler?: () => void;
  inputHandler: (content: string) => void;
  followCursor?: boolean;
  isEditing?: Signal<boolean>;
}): EditorComponent['init'] {
  const config: EditorComponent['init'] = {
    default_link_target: '_blank',
    inline: true,
    menubar: false,
    plugins: ['link', 'lists'],
    rel_list: [{ title: 'No Referrer', value: 'noreferrer' }],
    toolbar_location: 'top',
    toolbar_persist: true,
    toolbar: 'bold italic underline strikethrough | link | bullist numlist | removeformat',
    typeahead_urls: false,
    xss_sanitization: true,
    formats: {
      underline: { inline: 'u', exact: true },
      italic: { inline: 'em', exact: true },
      bold: { inline: 'strong', exact: true },
    },
    setup: (editor): void => {
      let lastToolboxPositionUpdate = 0;
      let hadSelectionChange = false;
      editor.on('init', () => {
        setCssPropertyValue('--text-area-tox-y', `${TOOLBAR_OFFSET_Y}px`);
        params.initHandler();
      });
      editor.on('click', (event: Event) => {
        // to prevent wrapper from handling the click
        event.stopPropagation();
      });
      editor.on('copy', (event: ClipboardEvent) => {
        if (!event.clipboardData) {
          return;
        }
        event.preventDefault();
        event.clipboardData.setData('text/plain', editor.selection.getContent({ format: 'text' }));
      });
      editor.on('paste', (event: ClipboardEvent) => {
        if (!event.clipboardData) {
          return;
        }
        event.preventDefault();
        const content = stripStyleTags(event.clipboardData.getData('text/plain'));
        editor.insertContent(content);
      });
      editor.on('blur', () => {
        if (!params.blurHandler) {
          return;
        }
        setTimeout(() => {
          editor.hide();
          params.blurHandler?.();
        });
      });
      editor.on('input', () => {
        params.inputHandler(editor.getContent());
      });

      if (params.followCursor) {
        editor.on('SelectionChange KeyUp MouseUp NodeChange', (e) => {
          if (!params.isEditing?.()) {
            return;
          }
          const now = new Date().getTime();
          if (e.type === 'selectionchange' && !hadSelectionChange) {
            hadSelectionChange = true;
          }
          if (hadSelectionChange && now - lastToolboxPositionUpdate > 100) {
            setTimeout(() => updateToolbarPosition(editor));
            lastToolboxPositionUpdate = now;
          }
        });
      }
    },
  };
  return config;
}

const TOOLBAR_OFFSET_Y = 15;

function updateToolbarPosition(editor: any): void {
  const rectTop = getEditorRect(editor);

  const scrollTop = editor.bodyElement.scrollTop;
  const editorTop = editor.getElement().getBoundingClientRect().top;
  const wrapperScrollTop =
    editor?.getElement?.()?.parentElement?.parentElement?.parentElement?.scrollTop ?? 0;

  const caretTop = Math.max(0, rectTop - (editorTop + scrollTop + wrapperScrollTop));
  const toxTop = caretTop - TOOLBAR_OFFSET_Y;
  setCssPropertyValue('--text-area-tox-y', `${toxTop}px`);
}

function getEditorRect(editor: any): number {
  if (isNullish(editor?.selection)) {
    return 0;
  }
  const range = editor.selection.getRng();
  const rt = range.getBoundingClientRect().top;
  if (rt > 0) {
    return rt;
  }

  const marker = editor.getDoc().createElement('span');
  marker.textContent = '\u200B'; // zero-width space
  marker.style.position = 'absolute';
  range.insertNode(marker);
  editor.selection.select(marker, false);
  const rect = marker.getBoundingClientRect();
  marker.remove();
  return rect.top;
}

export function stripStyleTags(text: string): string {
  if (!text.length || !text.includes('<style')) {
    return text;
  }
  return text.replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, '');
}
